import React, { useState, useEffect } from 'react';
import {
  PlusOutlined,
  DeleteOutlined,
  KeyOutlined,
  CopyOutlined,
  CheckOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { vpnAPI } from '@/api/endpoints/vpn.js';
import styles from '@/styles/pages/vpn/KeysPage.module.css';
import {useNavigate} from "react-router-dom";

const KeysPage = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [visibleUrls, setVisibleUrls] = useState({});

  // Загрузка ключей
  const fetchKeys = async () => {
    setLoading(true);
    try {
      const response = await vpnAPI.keys_get(false);
      if (response.success && response.data) {
        setKeys(response.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки ключей:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  // Создание ключа
  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    setCreating(true);
    try {
      const response = await vpnAPI.key_post({ name: newKeyName }, true);
      if (response.success) {
        setShowCreateModal(false);
        setNewKeyName('');
        fetchKeys();
      }
    } catch (error) {
      console.error('Ошибка создания ключа:', error);
    } finally {
      setCreating(false);
    }
  };

  // Удаление ключа
  const handleDeleteKey = async (keyId) => {
    try {
      const response = await vpnAPI.key_delete(keyId, true);
      if (response?.success) {
        setDeleteConfirm(null);
        fetchKeys();
      }
    } catch (error) {
      console.error('Ошибка удаления ключа:', error);
    }
  };

  // Удаление ключа
  const historyNavigate = async (keyId) => {
    navigate(`/services/vpn/keys/${keyId}`)
  };

  // Копирование access_url
  const handleCopyUrl = (keyId, url) => {
    navigator.clipboard.writeText(url);
    setCopiedId(keyId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Toggle видимости URL
  const toggleUrlVisibility = (keyId) => {
    setVisibleUrls(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'сегодня';
    if (diffDays === 1) return 'вчера';
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Сокращение URL для отображения
  const truncateUrl = (url, visible = false) => {
    if (visible) return url;
    if (!url) return '';
    const match = url.match(/ss:\/\/([^@]+)@/);
    if (match) {
      return `ss://${'•'.repeat(match[1].length)}@${url.split('@')[1]}`;
    }
    return url.substring(0, 50) + '...';
  };

  return (
    <div className={styles.container}>
      <div className={styles.bgGradient}></div>
      <div className={styles.bgGrid}></div>

      <div className={styles.content}>
        {/* Шапка */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <KeyOutlined className={styles.headerIcon} />
            <h1 className={styles.title}>Ключи доступа</h1>
            <span className={styles.keyCount}>{keys.length} ключей</span>
          </div>
          <button
            className={styles.createButton}
            onClick={() => setShowCreateModal(true)}
          >
            <PlusOutlined /> Создать ключ
          </button>
        </div>

        {/* Таблица ключей */}
        <div className={styles.tableContainer}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Загрузка ключей...</p>
            </div>
          ) : keys.length === 0 ? (
            <div className={styles.emptyState}>
              <KeyOutlined className={styles.emptyIcon} />
              <h3>Нет ключей</h3>
              <p>Создайте первый ключ для доступа к VPN</p>
              <button
                className={styles.emptyButton}
                onClick={() => setShowCreateModal(true)}
              >
                <PlusOutlined /> Создать ключ
              </button>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Название</th>
                  <th>Ключ (Access URL)</th>
                  <th>Создан</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id} className={styles.tableRow}>
                    <td className={styles.keyId}>#{key.id}</td>
                    <td className={styles.keyName}>{key.name}</td>
                    <td className={styles.keyUrl}>
                      <div className={styles.urlWrapper}>
                        <code className={styles.urlCode}>
                          {truncateUrl(key.access_url, visibleUrls[key.id])}
                        </code>
                        <div className={styles.urlActions}>
                          <button
                            className={styles.iconButton}
                            onClick={() => toggleUrlVisibility(key.id)}
                            title={visibleUrls[key.id] ? 'Скрыть' : 'Показать'}
                          >
                            {visibleUrls[key.id] ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                          </button>
                          <button
                            className={styles.iconButton}
                            onClick={() => handleCopyUrl(key.id, key.access_url)}
                            title="Копировать"
                          >
                            {copiedId === key.id ? <CheckOutlined /> : <CopyOutlined />}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className={styles.keyDate}>{formatDate(key.created_at)}</td>
                    <td className={styles.keyActions}>
                      <button
                        className={styles.deleteButton}
                        onClick={() => setDeleteConfirm(key)}
                        title="Удалить ключ"
                      >
                        <DeleteOutlined />
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => historyNavigate(key.id)}
                        title="История ключа"
                      >
                        <HistoryOutlined />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Модальное окно создания ключа */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Создать новый ключ</h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <label className={styles.modalLabel}>
                Название ключа
                <input
                  type="text"
                  className={styles.modalInput}
                  placeholder="Например: Мой ноутбук"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateKey()}
                  autoFocus
                />
              </label>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancel}
                onClick={() => setShowCreateModal(false)}
              >
                Отмена
              </button>
              <button
                className={styles.modalConfirm}
                onClick={handleCreateKey}
                disabled={!newKeyName.trim() || creating}
              >
                {creating ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {deleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Удалить ключ?</h2>
              <button
                className={styles.modalClose}
                onClick={() => setDeleteConfirm(null)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Вы действительно хотите удалить ключ <strong>“{deleteConfirm.name}”</strong>?</p>
              <p className={styles.modalWarning}>
                Это действие необратимо. Все подключения по этому ключу будут потеряны.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancel}
                onClick={() => setDeleteConfirm(null)}
              >
                Отмена
              </button>
              <button
                className={styles.modalConfirmDelete}
                onClick={() => handleDeleteKey(deleteConfirm.id)}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeysPage;